import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { v4 as uuidv4 } from 'uuid';
import { collectGroupInvariantViolations } from '../../stores/utils/group-row-guards';
import {
  DEFAULT_GROUP_COLOR,
  parseHexColor,
} from '../../components/table/src/utils/group-color';
import type { TableNode } from '../../stores/table-store';
import { toNodeNumberIdentity } from '../../stores/utils/node-number-identity';

export interface ImportedGroupMember {
  nodeNumber: number;
  order: number;
  /**
   * PSTUDIO-4637: the row itself, when the caller knows it. Node numbers can
   * repeat, so a caller grouping rows already on screen ("Suggest agents")
   * names them by id; CSV import has only the Node Number and leaves this off.
   */
  rowId?: string;
}

export interface ImportedGroupMeta {
  /**
   * The id of the group header the graft inserts. For a CSV it is a FRESH uuid
   * `extractCsvGroupMeta` made for the group (PSTUDIO-4549) — never the text of
   * the file's `Group Id`, which is only the key that tells which rows belong
   * together; so it cannot be looked up in the file. The in-place groupers
   * (Suggest agents, auto-grouping) make their own uuid too.
   */
  groupId: string;
  groupName: string;
  collapsed: boolean;
  groupColor: string;
  /**
   * PSTUDIO-4122 (S14): the trimmed `Group Color` cell, present only when it
   * was NOT empty and was replaced by the default because the group band cannot
   * paint it. Absent for an empty cell — see `resolveImportedGroupColor`.
   */
  invalidGroupColor?: string;
  members: ImportedGroupMember[];
  /**
   * PSTUDIO-4198: how many rows the file put in this group but
   * `extractCsvGroupMeta` could not read a usable Node Number from (non-numeric,
   * not a whole number, 0, outside the safe integer range, or blank). A negative
   * number is usable (PSTUDIO-4549). Absent when every row was readable.
   * `planImportedGroups` skips a group that has any, because restoring it would
   * build a group with fewer members than the file lists and report nothing.
   */
  unreadableRows?: number;
}

/**
 * PSTUDIO-4122: what `tableStore.applyImportedGrouping` did, told to the caller.
 *
 * It used to return `void` and raise its own English toast, which left the CSV
 * import unable to distinguish "grouping restored" from "grouping silently
 * dropped" — the whole of Defect A's user-visible symptom. A discriminated union
 * rather than a boolean because the three outcomes need different words, and the
 * partial one needs a count.
 *
 * `version-changed` carries both versions so the report can name them: the graft
 * targets the table by Node Number, and node numbers collide across solutions.
 */
export type ImportGroupingOutcome =
  | { status: 'nothing-to-do' }
  | { status: 'version-changed'; uploadedFor: string; storeIsOn: string }
  | { status: 'applied'; restored: number; skipped: string[] };

/**
 * PSTUDIO-4122: turn a `Group Color` cell into a colour the renderer can use.
 *
 * The exporter writes the group's stored colour verbatim, and a group whose
 * colour the user never picked has none stored — so a faithful export of it
 * carries an EMPTY cell. `draggable-row.tsx` reaches `DEFAULT_GROUP_COLOR`
 * through `rowSettings?.groupColor ?? DEFAULT_GROUP_COLOR`, which fires on
 * `undefined` and NOT on `''`; every style and attribute it derives from the
 * colour is then gated on the value being truthy, so an imported group used to
 * render with no band, no member guide and no `data-group-expanded` at all.
 *
 * Normalising here rather than widening the renderer's fallback keeps the empty
 * value out of the STORE: the colour is written into the synthesised header's
 * settings and persisted, so a later export of the imported group round-trips as
 * a real colour instead of reproducing the empty cell.
 *
 * Whitespace is trimmed for the same reason `Group Id` is (a hand-edited CSV is
 * the expected input): untrimmed, `'   '` is truthy, so the renderer would set
 * `--group-bg-color: '   '` — a band painting nothing, with the text tone
 * computed from an unparsable value.
 *
 * PSTUDIO-4122 (S14): a NON-empty cell is kept only when it is `#` followed by
 * 3, 6 or 8 hex digits. The `#` is required because `draggable-row.tsx` writes
 * the value into the CSS custom property `--group-bg-color` as it is, and
 * `197896` there paints nothing; the digit counts are the ones `parseHexColor`
 * reads, so `getGroupRowTextTone` judges the text tone from the colour actually
 * painted rather than falling back to `dark`. The presets in
 * `GROUP_COLOR_PRESETS` are all `#RRGGBB`. Anything else used to be stored
 * verbatim, so the imported group rendered with no band at all; it now gets the
 * default, and the rejected text is returned as `invalidGroupColor` so the
 * import can say so. An empty cell is defaulted WITHOUT being reported: it is
 * what a faithful export of a never-coloured group carries.
 */
export const resolveImportedGroupColor = (
  cell: string | undefined
): { groupColor: string; invalidGroupColor?: string } => {
  const trimmed = (cell ?? '').trim();
  if (!trimmed) return { groupColor: DEFAULT_GROUP_COLOR };
  if (trimmed.startsWith('#') && parseHexColor(trimmed)) {
    return { groupColor: trimmed };
  }
  return { groupColor: DEFAULT_GROUP_COLOR, invalidGroupColor: trimmed };
};

export interface ImportGroupPlan {
  headers: TableNode[];
  memberUpdates: { rowId: string; groupId: string }[];
  skipped: string[];
}

/**
 * PSTUDIO-4549: the Node Number of a grouped row, or undefined when the cell
 * names no number a group can hold.
 *
 * The question here is the one validation asks of a Node # cell — "is this a
 * number at all, and can it be compared to another row's?" — so it reuses that
 * one definition (`toNodeNumberIdentity`: a whole number in the safe range, never
 * 0, NEGATIVE allowed). It is NOT the `> 0` test this importer used before
 * (`nodeNumber <= 0`, added with PSTUDIO-3443's guards), which is what dropped
 * every negative member of a group on upload.
 *
 * The cell is read with `Number()` first, as this importer always read it, and
 * the resulting number is what `toNodeNumberIdentity` judges: `' -3 '` and
 * `'-4.0'` read as -3 and -4. Limitation, unchanged by this ticket: `Number()`
 * also reads `'1e3'` as 1000 and `'0x10'` as 16, which the identity's own string
 * grammar would reject.
 */
const readGroupedNodeNumber = (cell: string | undefined): number | undefined =>
  toNodeNumberIdentity(Number(cell));

/**
 * PSTUDIO-4549: the group a row declares, or undefined for a row in no group.
 *
 * `groupKey` is what tells the rows of one group apart in the file. Its prefix
 * keeps the two spaces of keys apart: `id:Billing` and `name:Billing` are
 * different groups. `idKey` is what `makeId` is handed (the Group Id, or the
 * Group Name of a group with no Group Id). A group keyed by its name carries the
 * trimmed name the key was made from, so `'Billing '` and `'Billing'` — one
 * group — do not show the stray space.
 */
const readDeclaredGroup = (
  r: Record<string, string>
): { groupKey: string; idKey: string; groupName: string } | undefined => {
  const declaredId = (r['Group Id'] ?? '').trim();
  const declaredName = (r['Group Name'] ?? '').trim();
  if (declaredId) {
    return {
      groupKey: `id:${declaredId}`,
      idKey: declaredId,
      groupName: r['Group Name'] ?? '',
    };
  }
  if (declaredName) {
    return {
      groupKey: `name:${declaredName}`,
      idKey: declaredName,
      groupName: declaredName,
    };
  }
  return undefined;
};

/**
 * PSTUDIO-3443: Parse CSV records (one per row) and group them, producing one
 * `ImportedGroupMeta` per group the file declares.
 * Members are sorted by `Group Order` ascending.
 *
 * A group is declared by its `Group Id`; a row with no Group Id but a
 * `Group Name` is declared by that name instead (PSTUDIO-4549 — a group written
 * by hand into a downloaded file has no id to give). A row with neither is not
 * in a group. Rows that share a Group Id (or, with no id, a Group Name) are one
 * group; the two kinds of key never meet, so a name cannot join a group whose
 * id merely spells it.
 *
 * The group's id is the header's node id, and it is ALWAYS a fresh uuid from
 * `makeId`, once per group: what the file says (a hand-written `g1`, a name,
 * even a uuid exported by Pro Studio) is only the KEY that says which rows
 * belong together, compared exactly as written.
 * The reason is not the id column's type: it is a plain string in the node
 * insert input (`Tenancy_Solution_Version_Nodes_Ps_Insert_Input`), and a
 * PSTUDIO-4549 run on candidate saved a header whose id was the hand-written
 * `agents`. The reason is the upsert: `UpdateProStudioNodes` resolves a conflict
 * by the primary key `solution_version_nodes_ps_pkey` and rewrites
 * `solutionVersionId` among its `update_columns` (`gql/pro-studio.gql`), so a
 * header id taken from the file would write over a node of another solution when
 * the file was exported from there. This closes that path for the import only;
 * what authorises a row for a request built by hand is the server's permission,
 * not this code.
 * `makeId` receives the key (the Group Id, or the Group Name of a group with no
 * Group Id) and is injectable so a test can pin the ids.
 *
 * Rows whose Node Number names no usable number (see `readGroupedNodeNumber`)
 * are skipped. A skipped row of a group that still has readable rows is counted
 * in that group's `unreadableRows`; a group with no readable row at all produces
 * no meta.
 */
export const extractCsvGroupMeta = (
  records: Record<string, string>[],
  makeId: (key: string) => string = () => uuidv4()
): ImportedGroupMeta[] => {
  const byGroup = new Map<string, ImportedGroupMeta>();
  const unreadableByGroup = new Map<string, number>();

  for (const r of records) {
    const declared = readDeclaredGroup(r);
    if (!declared) continue;
    const { groupKey } = declared;

    const nodeNumber = readGroupedNodeNumber(r['Node Number']);
    if (nodeNumber === undefined) {
      unreadableByGroup.set(
        groupKey,
        (unreadableByGroup.get(groupKey) ?? 0) + 1
      );
      continue;
    }

    const member = { nodeNumber, order: Number(r['Group Order']) || 0 };

    const existing = byGroup.get(groupKey);
    if (existing) {
      existing.members.push(member);
    } else {
      byGroup.set(groupKey, {
        groupId: makeId(declared.idKey),
        groupName: declared.groupName,
        // Group Collapsed is a group-level attribute; take the first member's value (all rows of a group carry the same value in a well-formed export).
        collapsed: r['Group Collapsed'] === '1',
        ...resolveImportedGroupColor(r['Group Color']),
        members: [member],
      });
    }
  }

  for (const [groupKey, meta] of byGroup) {
    meta.members.sort((a, b) => a.order - b.order);
    const unreadable = unreadableByGroup.get(groupKey);
    if (unreadable) meta.unreadableRows = unreadable;
  }

  return [...byGroup.values()];
};

/**
 * PSTUDIO-3443: Given parsed group meta and the current post-reload rows,
 * match each member by Node Number (or by `rowId` when given), synthesize a
 * `TableNode` group header (id = the meta's `groupId`), and produce the
 * member-update list.
 *
 * Groups with no matched members, any unmatched member, any row the file named
 * but `extractCsvGroupMeta` could not read (`unreadableRows`), or an invalid
 * invariant probe are pushed to `skipped` and dropped. A group of ONE member is
 * a valid group (PSTUDIO-4198); what it must not do is stand in for a larger
 * group that lost rows on the way in.
 */
export const planImportedGroups = (
  groupMeta: ImportedGroupMeta[],
  currentRows: TableNode[]
): ImportGroupPlan => {
  const byNumber = new Map<number, TableNode>();
  const byId = new Map<string, TableNode>();
  for (const row of currentRows) {
    byId.set(row.id, row);
    if (typeof row.nodeNumber === 'number') {
      byNumber.set(row.nodeNumber, row);
    }
  }

  const headers: TableNode[] = [];
  const memberUpdates: { rowId: string; groupId: string }[] = [];
  const skipped: string[] = [];

  for (const meta of groupMeta) {
    const matched = meta.members.map((m) =>
      m.rowId !== undefined ? byId.get(m.rowId) : byNumber.get(m.nodeNumber)
    );

    if (
      matched.length < 1 ||
      meta.unreadableRows ||
      matched.some((row) => !row)
    ) {
      skipped.push(meta.groupId);
      continue;
    }

    const memberIds = (matched as TableNode[]).map((row) => row.id);

    const header = {
      id: meta.groupId,
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
      nodeNumber: null,
      settings: {
        subRows: memberIds,
        groupName: meta.groupName,
        groupExpanded: !meta.collapsed,
        groupColor: meta.groupColor,
      },
    } as unknown as TableNode;

    // Build a probe with the header + members-with-groupId set so
    // collectGroupInvariantViolations can check bidirectional subRows↔groupId.
    const probe = [
      header,
      ...(matched as TableNode[]).map((row) => ({
        ...row,
        settings: { ...row.settings, groupId: meta.groupId },
      })),
    ];

    if (collectGroupInvariantViolations(probe).length > 0) {
      skipped.push(meta.groupId);
      continue;
    }

    headers.push(header);
    for (const rowId of memberIds) {
      memberUpdates.push({ rowId, groupId: meta.groupId });
    }
  }

  return { headers, memberUpdates, skipped };
};
